/** @module Specification/BOM/EditMappingController */
define([
	"dojo/_base/declare",
	"dojo/on",
	"dojo/store/Memory",
	"underscore",
	"csi",
	"csi/action/PopupController",
	"csi/locale",
	"csi/store/GroupQueryEngine",
	"csi/xml",
	"../SizeVariationParams"
], function(declare, on, Memory, _, csi, PopupController, locale, GroupQueryEngine, xml, {SIZE_SPEC_PARAMS}) {

	/**
	 * Return name for placement's section
	 * @param {QueryResult} qr
	 * @return {string}
	 */
	function getGroupName(qr) {
		var value = qr.parentResult.getGroupValue();
		return (value == xml.VALUE_BLANK) ? locale.resource(value) : value;
	}

	/**
	 * Return name for placement store which is combination of Placement and Actual (Product) names
	 * @param {QueryResult} qr
	 * @param {Cache} cache
	 * @return {string|null}
	 */
	function getName(qr, cache) {
		const pm = qr.getNode();
		const actual = cache.get(pm.Actual);

		if (qr.bCompareNode || !actual || !pm.OKVariationByBOMSize) {
			return null;
		}

		const actualName = locale.getNodeName(actual);
		const name = locale.getNodeStoredName(pm, cache);
		return name ? (name + ": " + actualName) : actualName;
	}

	return declare(PopupController, {
		/** @property {string} initialVariationNodeType - variation node type to show initially in popup */
		initialVariationNodeType: null,

		/** @property {Object} _unloadHandle - event handler for page being unloaded before closing */
		_unloadHandle: null,

		/**
		 * Return list of modified URLs used by PostAction
		 * @return {string[]}
		 */
		getModifiedUrls: function() {
			return _.keys(this.popupView.modifiedPlacements);
		},

		/**
		 * @override
		 * Pass BOM properties to popup view
		 */
		onCreatePopupParams: function(params) {
			var self = this;
			var view = this.widget;

			params.bomSizeSet = view.bomSizeSet;
			params.bomActiveSizes = view.bomActiveSizes;
			params.bomActiveSizes2 = view.bomActiveSizes2;
			params.initialVariationNodeType = this.initialVariationNodeType;
			params.displaySizeSpecs = this._isSizeSpecInView();

			// determine placements store
			var cache = view.cacheView;
			let results = view.getQueryResults();
			var bGroup = view.isGroupBySection();
			var n = 0;

			var data = csi.transform(results, (qr) => {
				if (qr.bResultNode) {
					const name = getName(qr, cache);
					if (name) {
						return {
							group: bGroup ? getGroupName(qr) : null,
							id: qr.url,
							index: n++,
							name
						};
					}
				}

				return null;
			});

			if (n > 1) {
				var storeOptions = {
					cssProperty: "css",
					data: data
				};

				if (bGroup) {
					storeOptions.queryEngine = GroupQueryEngine;
				}

				params.placementTotal = n;
				params.placementStore = new Memory(storeOptions);
			}

			// add event handler to make sure any changes are saved before browser is closed/refreshed
			this._unloadHandle = on(window, "beforeunload", function() {
				if (self.popupView) {
					self.resumeProcess();
					self.process();
				}
			});
		},

		/**
		 * @override
		 * Add the dependency path to load the PartMaterialSize's PMSSize and PartMaterialSizeSpec's PMSSizeSpec nodes
		 */
		onDependencyPaths: function(paths) {
			paths.push("Child:PartMaterialSizes/Child:PMSSize");
			paths.push("Child:PartMaterialSizeSpecs/Child:PMSSizeSpec");
			this.inherited(arguments);
		},

		/**
		 * @override
		 * Send the data to server to process
		 */
		toWriter: function() {
			this._unloadHandle.remove();
			this.popupView.onActionSizeChanges(this);
		},

		/**
		 * @override
		 * Refresh all modified rows
		 */
		postProcessSuccess: function() {
			const urls = this.getModifiedUrls();
			if (urls) {
				const {m_cache, widget} = this;
				const nodes = m_cache.getNodes(urls);
				const pms = nodes.filter((node) => node && node.$Type === "PartMaterial");

				for (const pm of pms) {
					const row = widget.getRefNodeByUrl(pm.$URL);
					if (row) {
						widget.refreshHierarchyRow(row);
					}
				}
			}
		},

		/**
		 * Return true if size spec appears in any matrix in view
		 * @return {boolean}
		 */
		_isSizeSpecInView() {
			let bFound = false;

			this.widget.columnData.traverse(true, (c, sc) => {
				const spec = sc || c;

				if (spec && spec.heading && (spec.heading.m_id === SIZE_SPEC_PARAMS.attr)) {
					bFound = true;
				}
			});

			return bFound;
		}
	});
});